import React, { useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  ChevronDown,
  Users,
  TrendingUp,
  Wrench,
  MessageCircle,
  FileText,
  HeartHandshake,
  Mail,
  Building2,
  ShieldCheck,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";
import logoLyraxDark from '@/assets/images/logo-lyrax.png'
import logoLyraxWhite from '@/assets/images/logo-lyrax-white.png'
import logoLyrax from '@/styles/assets/logo-lyrax.png'
import { useAuth } from "@/hooks/useAuth";
import { isSidebarKeyAllowed } from "@/data/sidebarMenu";

export interface MenuItem {
  key: string;
  label: string;
  icon?: LucideIcon;
  path?: string;
  children?: MenuItem[];
  requiredPermissions?: string[];
  /** Tenant feature key / channel slug required (e.g. virtualpos) — hides item if missing */
  requiredFeature?: string;
  /** Premium module: always visible; shows PRO badge when feature missing */
  premiumFeature?: string;
  /** Ecosystem product (not Lyrax Pro); always visible; locked when feature missing */
  productFeature?: string;
}

/** Funcionalidades habilitadas / core del producto */
const menuItems: MenuItem[] = [
  {
    key: "dashboards",
    label: "Dashboards",
    icon: LayoutDashboard,
    children: [
      { key: "dashboard.general", label: "General", path: "/dashboard/general", requiredPermissions: ["dashboard:general"] },
      { key: "dashboard.suscriptores", label: "Suscriptores", path: "/dashboard/suscriptores", requiredPermissions: ["dashboard:suscriptores"] },
      { key: "dashboard.financiero", label: "Financiero", path: "/dashboard/financiero", requiredPermissions: ["dashboard:financiero"] },
    ],
  },
  {
    key: "subscribers",
    label: "Gestión de Suscriptores",
    icon: Users,
    children: [
      { key: "subscribers.cartera", label: "Cartera", path: "/subscribers/cartera", requiredPermissions: ["dashboard:suscriptores"] },
      { key: "subscribers.virtualpos", label: "VirtualPos", path: "/subscribers/virtual-pos", requiredPermissions: ["virtualpos:read"], requiredFeature: "virtualpos" },
      { key: "subscribers.payku", label: "Payku", path: "/subscribers/payku", requiredPermissions: ["payku:read"], requiredFeature: "payku" },
      { key: "subscribers.mandatos", label: "Mandatos", path: "/subscribers/mandatos", requiredPermissions: ["mandatos:read"], requiredFeature: "mandatos" },
      { key: "subscribers.donando", label: "Donando", path: "/subscribers/donando", requiredFeature: "donando" },
    ],
  },
  {
    key: "management",
    label: "Control de Gestión",
    icon: TrendingUp,
    children: [
      { key: "management.forecast", label: "Forecast", path: "/management/forecast" },
      { key: "management.presupuesto", label: "Presupuesto", path: "/management/presupuesto" },
      { key: "management.proyecciones", label: "Proyecciones", path: "/management/proyecciones" },
    ],
  },
  {
    key: "mantenants",
    label: "Mantenedores",
    icon: Wrench,
    children: [
      { key: "mantenants.usuarios", label: "Usuarios", path: "/mantenants/usuarios" },
      { key: "mantenants.estados", label: "Estados", path: "/mantenants/estados" },
      { key: "mantenants.bancos", label: "Bancos", path: "/mantenants/bancos" },
    ],
  },
  {
    key: "reports",
    label: "Reportes",
    icon: FileText,
    path: "/reports",
  },
  {
    key: "settings",
    label: "Configuración",
    icon: Settings,
    children: [
      { key: "settings.usuarios", label: "Usuarios", path: "/settings/usuarios", requiredPermissions: ["settings:usuarios", "users:read"] },
      { key: "settings.integrations", label: "Integraciones", path: "/settings/integrations", requiredPermissions: ["settings:integrations"] },
      { key: "settings.integration-logs", label: "Logs de Integración", path: "/settings/integration-logs", requiredPermissions: ["settings:integration-logs"] },
    ],
  },
];

/** Módulos Pro — visibles si el plan los incluye; badge si falta la feature */
const proMenuItems: MenuItem[] = [
  {
    key: "pro.donaciones",
    label: "Donaciones",
    icon: HeartHandshake,
    path: "/donaciones",
    premiumFeature: "donaciones",
  },
  {
    key: "pro.email",
    label: "Email",
    icon: Mail,
    path: "/email",
    premiumFeature: "email",
  },
  {
    key: "pro.chatbot",
    label: "Asistente IA",
    icon: MessageCircle,
    path: "/chatbot",
    premiumFeature: "chatbot",
  },
];

/** Productos del ecosistema — no son Lyrax Pro */
const productMenuItems: MenuItem[] = [
  {
    key: "product.erp",
    label: "ERP",
    icon: Building2,
    path: "/erp",
    productFeature: "erp",
  },
];

function Icon({ icon: IconComp, className }: { icon?: LucideIcon; className?: string }) {
  return IconComp ? <IconComp className={className} /> : null;
}

function ProBadge() {
  return (
    <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
      PRO
    </span>
  );
}

function ProductBadge() {
  return (
    <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-slate-500">
      Producto
    </span>
  );
}

function itemAllowed(
  item: MenuItem,
  hasPermission: (required?: string[]) => boolean,
  hasFeature: (required?: string) => boolean,
  hasPlan: (key?: string) => boolean,
): boolean {
  return hasPermission(item.requiredPermissions) && hasFeature(item.requiredFeature) && hasPlan(item.key);
}

function filterMenuItems(
  items: MenuItem[],
  hasPermission: (required?: string[]) => boolean,
  hasFeature: (required?: string) => boolean,
  hasPlan: (key?: string) => boolean,
): MenuItem[] {
  return items
    .map((item) => {
      if (item.children?.length) {
        const filteredChildren = item.children.filter((child) =>
          itemAllowed(child, hasPermission, hasFeature, hasPlan),
        );
        if (filteredChildren.length === 0) {
          return item.path && itemAllowed(item, hasPermission, hasFeature, hasPlan)
            ? { ...item, children: [] }
            : null;
        }
        return { ...item, children: filteredChildren };
      }
      return itemAllowed(item, hasPermission, hasFeature, hasPlan) ? item : null;
    })
    .filter((item): item is MenuItem => item !== null);
}

type SidebarProps = { collapsed: boolean };

export const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const [openMenus, setOpenMenus] = useState<string[]>([
    "Dashboards",
    "Gestión de Suscriptores",
    "Control de Gestión",
    "Mantenedores",
  ]);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const calculatePosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.right + 8,
    };
  };

  const handleMouseEnter = (itemLabel: string) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const ref = menuItemRefs.current.get(itemLabel);
    if (ref) {
      setMenuPosition(calculatePosition(ref));
    }
    setHoveredMenu(itemLabel);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setMenuPosition(null);
    }, 100);
  };

  const permissionSet = useMemo(() => {
    const permissions = user?.role?.permissions ?? [];
    return new Set(permissions.map((permission) => permission.name));
  }, [user]);

  const featureSet = useMemo(() => {
    const features = user?.features ?? [];
    const channels = user?.channels ?? [];
    return new Set([...features, ...channels]);
  }, [user]);

  const hasPermission = (required?: string[]) => {
    if (!required || required.length === 0) return true;
    return required.some((permission) => permissionSet.has(permission));
  };

  const hasFeature = (required?: string) => {
    if (!required) return true;
    if (!user?.features && !user?.channels) return true;
    return featureSet.has(required);
  };

  const hasPlan = (key?: string) => {
    if (!key) return true;
    return isSidebarKeyAllowed(user?.sidebarMenu, key);
  };

  const isPremiumLocked = (item: MenuItem) => {
    if (!item.premiumFeature) return false;
    if (!user?.features && !user?.channels) return false;
    return !featureSet.has(item.premiumFeature);
  };

  const isProductLocked = (item: MenuItem) => {
    if (!item.productFeature) return false;
    if (!user?.features && !user?.channels) return false;
    return !featureSet.has(item.productFeature);
  };

  const isLocked = (item: MenuItem) => isPremiumLocked(item) || isProductLocked(item);

  const lockedTitle = (item: MenuItem) => {
    if (isProductLocked(item)) {
      return `${item.label} — Producto del ecosistema Lyrax`;
    }
    if (isPremiumLocked(item)) {
      return `${item.label} — Esta característica está incluida en Lyrax Pro`;
    }
    return item.label;
  };

  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems, hasPermission, hasFeature, hasPlan),
    [permissionSet, featureSet, user],
  );

  const filteredProMenuItems = useMemo(
    () => filterMenuItems(proMenuItems, hasPermission, hasFeature, hasPlan),
    [permissionSet, featureSet, user],
  );

  const filteredProductMenuItems = useMemo(
    () => filterMenuItems(productMenuItems, hasPermission, hasFeature, hasPlan),
    [permissionSet, featureSet, user],
  );

  const allMenuItems = useMemo(
    () => [...filteredMenuItems, ...filteredProMenuItems, ...filteredProductMenuItems],
    [filteredMenuItems, filteredProMenuItems, filteredProductMenuItems],
  );

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label],
    );
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = !!item.children?.length;
    const isOpen = openMenus.includes(item.label);

    if (collapsed) {
      if (hasChildren) {
        return (
          <div
            key={item.label}
            className="relative group"
            ref={(el) => {
              if (el) menuItemRefs.current.set(item.label, el);
              else menuItemRefs.current.delete(item.label);
            }}
            onMouseEnter={() => handleMouseEnter(item.label)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="flex items-center justify-center w-full p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={item.label}
            >
              <Icon icon={item.icon} className="w-5 h-5" />
              {isLocked(item) && (
                <span
                  className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ${
                    isProductLocked(item) ? "bg-slate-600" : "bg-orange-500"
                  }`}
                />
              )}
            </button>
          </div>
        );
      }

      return (
        <NavLink
          key={item.label}
          to={item.path || "#"}
          className={({ isActive }) =>
            `relative flex items-center justify-center p-2.5 rounded-lg transition-colors ${
              isActive
                ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`
          }
          title={lockedTitle(item)}
        >
          <Icon icon={item.icon} className="w-5 h-5" />
          {isLocked(item) && (
            <span
              className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ${
                isProductLocked(item) ? "bg-slate-600" : "bg-orange-500"
              }`}
            />
          )}
        </NavLink>
      );
    }

    return (
      <div key={item.label}>
        {hasChildren ? (
          <button
            onClick={() => toggleMenu(item.label)}
            className="flex items-center gap-3 w-full px-3 py-2 rounded hover:bg-lyrax-surface-hover hover:text-lyrax-primary transition-colors duration-200 text-lyrax-text-secondary"
          >
            <Icon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
            {isPremiumLocked(item) && <ProBadge />}
            {isProductLocked(item) && <ProductBadge />}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        ) : (
          <NavLink
            to={item.path || "#"}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 w-full px-3 py-2 rounded hover:bg-lyrax-surface-hover hover:text-lyrax-primary transition-colors duration-200 ${
                isActive
                  ? "bg-lyrax-bg text-lyrax-primary font-bold shadow-inner"
                  : "text-lyrax-text-secondary"
              }`
            }
          >
            <Icon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 text-sm font-medium">{item.label}</span>
            {isPremiumLocked(item) && (
              <>
                <ProBadge />
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-3 py-2 text-xs text-white shadow-lg group-hover:block dark:bg-slate-700"
                >
                  Esta característica está incluida en Lyrax Pro
                  <span className="absolute right-full top-1/2 -mt-1 border-4 border-transparent border-r-slate-800 dark:border-r-slate-700" />
                </span>
              </>
            )}
            {isProductLocked(item) && (
              <>
                <ProductBadge />
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-3 py-2 text-xs text-white shadow-lg group-hover:block dark:bg-slate-700"
                >
                  Producto del ecosistema Lyrax
                  <span className="absolute right-full top-1/2 -mt-1 border-4 border-transparent border-r-slate-800 dark:border-r-slate-700" />
                </span>
              </>
            )}
          </NavLink>
        )}

        {hasChildren && isOpen && !collapsed && (
          <div className="ml-9 mt-1 space-y-0.5">
            {item.children!.map((child) => (
              <NavLink
                key={child.label}
                to={child.path || "#"}
                className={({ isActive }) => {
                  const activeClass = isActive
                    ? "text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20"
                    : "text-gray-600 dark:text-gray-400";
                  return `flex items-center px-3 py-2 rounded-lg text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${activeClass}`;
                }}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${
        collapsed ? "w-20" : "w-64"
      } bg-lyrax-surface flex flex-col transition-all duration-300`}
    >
      <div className="h-20 flex items-center justify-center">
        {collapsed ? (
          <div className="flex items-center justify-center h-10 w-10 bg-lyrax-bg rounded-xl shadow-inner border border-lyrax-primary/20 transition-all duration-300">
            <span className="text-2xl font-bold text-lyrax-primary tracking-tighter">L</span>
          </div>
        ) : (
          <>
            <img
              src={logoLyraxDark}
              alt="Lyrax Logo"
              className="h-12 w-auto object-contain dark:hidden transition-all duration-300"
            />
            <img
              src={logoLyraxWhite}
              alt="Lyrax Logo"
              className="h-12 w-auto object-contain hidden dark:block transition-all duration-300"
            />
          </>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-visible">
        {filteredMenuItems.map(renderMenuItem)}

        {filteredProMenuItems.length > 0 && (
          <div className={collapsed ? "mt-3 pt-3 border-t border-orange-500/20" : "mt-4 pt-3"}>
            {collapsed ? (
              <div
                className="mb-2 flex justify-center"
                title="Lyrax Pro"
              >
                <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                  PRO
                </span>
              </div>
            ) : (
              <div className="mb-2 flex items-center gap-2 px-3">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-600 dark:text-orange-400">
                  Lyrax Pro
                </span>
                <span className="h-px flex-1 bg-orange-500/25" />
              </div>
            )}
            <div className="space-y-1">{filteredProMenuItems.map(renderMenuItem)}</div>
          </div>
        )}

        {filteredProductMenuItems.length > 0 && (
          <div className={collapsed ? "mt-3 pt-3 border-t border-slate-400/30" : "mt-4 pt-3"}>
            {collapsed ? (
              <div className="mb-2 flex justify-center" title="Productos">
                <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white dark:bg-slate-500">
                  PROD
                </span>
              </div>
            ) : (
              <div className="mb-2 flex items-center gap-2 px-3">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-400">
                  Productos
                </span>
                <span className="h-px flex-1 bg-slate-400/30" />
              </div>
            )}
            <div className="space-y-1">{filteredProductMenuItems.map(renderMenuItem)}</div>
          </div>
        )}

        {user?.isSuperAdmin && (
          <div className={collapsed ? "mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" : "mt-4 pt-3"}>
            {!collapsed && (
              <div className="mb-2 flex items-center gap-2 px-3">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                  Administración
                </span>
                <span className="h-px flex-1 bg-gray-300/60 dark:bg-gray-600" />
              </div>
            )}
            <NavLink
              to="/admin/planes"
              title="Menú"
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"} rounded-lg transition-colors ${
                  isActive
                    ? "bg-lyrax-bg text-lyrax-primary font-bold"
                    : "text-lyrax-text-secondary hover:bg-lyrax-surface-hover hover:text-lyrax-primary"
                }`
              }
            >
              <ShieldCheck className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Menú</span>}
            </NavLink>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {collapsed ? (
          <div className="flex items-center justify-center">
            <img src={logoLyrax} alt="Lyrax" className="h-6 object-contain opacity-60" />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span>Powered by</span>
            <img src={logoLyrax} alt="Lyrax" className="h-5 object-contain" />
          </div>
        )}
      </div>

      {hoveredMenu && menuPosition && (
        createPortal(
          <div
            className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 min-w-[180px] py-2 z-[9999] cursor-default"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
              setHoveredMenu(hoveredMenu);
            }}
            onMouseLeave={() => {
              hideTimeoutRef.current = setTimeout(() => {
                setHoveredMenu(null);
                setMenuPosition(null);
              }, 100);
            }}
          >
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              {hoveredMenu}
            </div>
            {allMenuItems
              .find((item) => item.label === hoveredMenu)
              ?.children?.map((child) => (
                <NavLink
                  key={child.label}
                  to={child.path || "#"}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                      isActive
                        ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium"
                        : ""
                    }`
                  }
                >
                  {child.label}
                </NavLink>
              ))}
          </div>,
          document.body,
        )
      )}
    </aside>
  );
};
